import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import BugReportIcon from '@mui/icons-material/BugReport'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { feedbackApi, ApiError } from '../lib/api'

const MESSAGE_MAX_LEN = 2000

type FeedbackType = 'bug' | 'feature' | 'other'

interface FeedbackDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Modal form for submitting feedback. Page context (the current pathname) is
 * captured automatically. When the user is signed in with a verified email,
 * the email field is omitted entirely — the backend already has it via the
 * JWT and asking again is friction. Otherwise the field is shown so anonymous
 * users can opt in to a reply.
 */
export function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const location = useLocation()

  const [type, setType] = useState<FeedbackType>('other')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showThanks, setShowThanks] = useState(false)

  const signedInEmail = user?.email?.trim() || ''
  const hideEmailField = !!signedInEmail

  // Reset form whenever the dialog re-opens — but keep the user's last picked
  // type so frequent bug reporters don't have to re-pick. Email defaults to
  // the signed-in account when present.
  useEffect(() => {
    if (open) {
      setMessage('')
      setEmail(signedInEmail)
      setError(null)
    }
  }, [open, signedInEmail])

  const remaining = useMemo(() => MESSAGE_MAX_LEN - message.length, [message])
  const trimmedMessage = message.trim()
  const canSubmit = trimmedMessage.length > 0 && trimmedMessage.length <= MESSAGE_MAX_LEN && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await feedbackApi.send({
        type,
        message: trimmedMessage,
        email: hideEmailField ? undefined : (email.trim() || undefined),
        page: location.pathname,
      })
      setShowThanks(true)
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t('feedback.errorGeneric'))
      } else {
        setError(t('feedback.errorGeneric'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="feedback-dialog-title"
      >
        <DialogTitle id="feedback-dialog-title">{t('feedback.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('feedback.subtitle')}
            </Typography>

            <Box>
              <ToggleButtonGroup
                value={type}
                exclusive
                onChange={(_, next: FeedbackType | null) => {
                  if (next) setType(next)
                }}
                size="small"
                aria-label="feedback type"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value="bug">
                  <BugReportIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t('feedback.typeBug')}
                </ToggleButton>
                <ToggleButton value="feature">
                  <LightbulbIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t('feedback.typeFeature')}
                </ToggleButton>
                <ToggleButton value="other">
                  <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t('feedback.typeOther')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              label={t('feedback.messageLabel')}
              value={message}
              onChange={(e) => {
                // Soft cap — TextField will still accept beyond max via paste,
                // but we slice so the helper count and submit guard agree.
                const next = e.target.value.slice(0, MESSAGE_MAX_LEN)
                setMessage(next)
              }}
              multiline
              rows={4}
              fullWidth
              required
              autoFocus
              slotProps={{ htmlInput: { maxLength: MESSAGE_MAX_LEN } }}
              helperText={t('feedback.messageHelper', { remaining })}
            />

            {!hideEmailField && (
              <TextField
                label={t('feedback.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                helperText={t('feedback.emailHelper')}
              />
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            {t('feedback.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit}
          >
            {submitting ? t('feedback.sending') : t('feedback.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showThanks}
        autoHideDuration={4000}
        onClose={() => setShowThanks(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setShowThanks(false)}
          sx={{ width: '100%' }}
        >
          {t('feedback.thanks')}
        </Alert>
      </Snackbar>
    </>
  )
}
